import AudiencePage, {
  type AudienceFaq,
  type AudienceItem,
  type AudienceStat,
} from "@/components/public/audience-page"
import type { SeoMeta } from "@/types"

const PAINS: AudienceItem[] = [
  {
    title: "Scottish sales run to a different clock",
    description:
      "Home Report required before marketing, offers-over pricing, closing dates, missives, LBTT instead of stamp duty \u2014 the English-market video templates get the language wrong. QuiteRight uses Scottish property terminology out of the box.",
  },
  {
    title: "Closing-date momentum is won on social",
    description:
      "Setting a closing date concentrates interest into a narrow window. Social video ads in the week before the closing date drive viewings and competing bids \u2014 the difference between one offer and a bidding contest.",
  },
  {
    title: "The Home Report is underused marketing",
    description:
      "Most agents treat the Home Report as a compliance document. It's actually a marketing asset \u2014 valuation, EPC, surveyor notes. Weaved into a video ad with the right captions, it builds buyer confidence instantly.",
  },
  {
    title: "Rural and island instructions need their own reach",
    description:
      "A Highland or island instruction can sit on the portal for months. Social video ads let you reach relocators, lifestyle buyers and overseas Scots who would never check Rightmove Scotland daily.",
  },
]

const FEATURES: AudienceItem[] = [
  { title: "Scottish property vocabulary", description: "\u201COffers over,\u201D \u201Cfixed price,\u201D \u201Cupset price,\u201D \u201CHome Report available\u201D \u2014 the script engine uses Scottish terminology correctly, not South-East defaults." },
  { title: "Home Report integration", description: "Attach the Home Report to a listing and QuiteRight pulls the EPC rating and Home Report valuation into the video as trust signals." },
  { title: "Closing date countdown template", description: "A dedicated template for closing-date weeks, counting down to the deadline to drive competing offers." },
  { title: "ESPC portal support", description: "Alongside Rightmove and OnTheMarket, QuiteRight reads ESPC listing URLs \u2014 the Scottish Solicitors' Property Centre portal used widely in Edinburgh and central Scotland." },
  { title: "Gaelic and bilingual options", description: "Bilingual script options for Gaelic-speaking markets (Western Isles, Skye, north-west Highlands) available on request." },
  { title: "Rural, island and city-centre tuning", description: "Tenement flats in Glasgow, a steading in Aberdeenshire, a cottage on Skye \u2014 the template engine handles them all, not just standard suburban stock." },
]

const STATS: AudienceStat[] = [
  { value: "Home", label: "Report data pulled in as trust signals", tone: "accent" },
  { value: "60s", label: "per instruction, from URL to finished ad" },
  { value: "ESPC", label: "plus Rightmove and OnTheMarket supported" },
]

const FAQ: AudienceFaq[] = [
  {
    q: "Does QuiteRight understand the Scottish legal process?",
    a: "Yes. The script engine uses Scottish property terminology correctly \u2014 offers over, fixed price, upset price, closing dates, missives, LBTT. It won't ask buyers to \u201Cmake an offer asking price\u201D or reference stamp duty, which would sound wrong to a Scottish audience.",
  },
  {
    q: "Can I attach the Home Report to a QuiteRight listing?",
    a: "Yes. Upload the Home Report (or its key data) to a listing and QuiteRight uses the EPC rating and Home Report valuation as captions / voiceover trust signals. Buyers see that the property is Home Report ready, which builds confidence.",
  },
  {
    q: "Does QuiteRight support ESPC listings?",
    a: "Yes. ESPC listing URLs can be scraped like Rightmove or OnTheMarket ones. You can also upload ESPC listings that haven't been mirrored to Rightmove \u2014 particularly common for solicitor agents in Edinburgh and surrounding areas.",
  },
  {
    q: "Is there a closing-date specific template?",
    a: "Yes. When you set a closing date on a listing, QuiteRight offers a closing-date template variant \u2014 countdown timer, \u201Cnote of interest deadline\u201D messaging, and urgency-tuned voiceover. Designed for driving competing offers in the final week.",
  },
  {
    q: "What about Gaelic or bilingual scripts?",
    a: "Bilingual Gaelic/English scripts are available on request for instructions in Gaelic-speaking markets (Western Isles, Skye, parts of the Highlands). Contact us to activate bilingual rendering on your account.",
  },
]

export default function ForScottishAgents({ meta }: { meta: SeoMeta }) {
  return (
    <AudiencePage
      meta={meta}
      breadcrumbName="For Scottish Estate Agents"
      heroEyebrow="For Scottish estate agents"
      heroHeadline="Offers over, closing dates, Home Reports \u2014 video that speaks Scottish"
      heroSubhead="Scottish sales run to a different clock. QuiteRight uses the correct Scottish property terminology out of the box, supports ESPC listings, and gives every closing date the social push it deserves \u2014 without an English-market template getting the language wrong."
      heroBadgeSmall="Built for Scotland"
      heroBadgeLarge="not South-East"
      painsHeadline="Why Scottish instructions need their own video templates"
      pains={PAINS}
      featuresEyebrow="Built for Scottish sales"
      featuresHeadline="Scottish terminology, ESPC support, closing-date tooling"
      features={FEATURES}
      statsHeadline="Scottish property video, in numbers"
      stats={STATS}
      faq={FAQ}
      ctaHeadline="Closing date, Home Report, ESPC \u2014 covered"
      ctaBody="Built for Scottish estate agents from the ground up. \u00A3499 a month fully managed, unlimited listings."
      relatedKey="for/scottish-agents"
    />
  )
}
